import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Youtube, ArrowRight, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FOOTER } from '@/constants/testIds';
import { AAWTA } from '@/lib/aawta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

const NAV_LINKS = [
  ['Home', '#home'],
  ['Summit Overview', '#summit'],
  ['Agenda', '#agenda'],
  ['Speakers', '#speakers'],
  ['Sponsor Showcase', '#sponsor-showcase'],
  ['Exhibitors', '#exhibitors'],
  ['Gallery', '#gallery'],
  ['FAQ', '#faq'],
  ['Venue & Hotels', '#venue'],
  ['Downloads', '#downloads'],
  ['Contact', '#contact'],
];

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('You are on the list. Welcome to the movement.');
      setEmail('');
    } catch (err) {
      toast.error('Something went wrong. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const socials = [
    { id: FOOTER.socialFacebook, Icon: Facebook, href: AAWTA.social.facebook, label: 'Facebook' },
    { id: FOOTER.socialInstagram, Icon: Instagram, href: AAWTA.social.instagram, label: 'Instagram' },
    { id: FOOTER.socialLinkedin, Icon: Linkedin, href: AAWTA.social.linkedin, label: 'LinkedIn' },
    { id: FOOTER.socialYoutube, Icon: Youtube, href: AAWTA.social.youtube, label: 'YouTube' },
  ];

  return (
    <footer data-testid={FOOTER.root} className="bg-aawta-navy-deep text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-navy opacity-20" />
      <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-aawta-magenta/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
        {/* Big CTA strip */}
        <div className="mb-16 grid md:grid-cols-2 gap-3">
          <Button
            data-testid="footer-cta-register"
            onClick={() => openExternal(AAWTA.eventbriteUrl)}
            className="rounded-2xl bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium h-16 text-[15px] shadow-aawta-magenta group"
          >
            Register Now on Eventbrite
            <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Button>
          <Button
            data-testid="footer-cta-sponsor"
            onClick={() => openExternal(AAWTA.zeffySponsorUrl)}
            variant="outline"
            className="rounded-2xl bg-white text-aawta-navy hover:bg-white/90 border-white font-medium h-16 text-[15px] group"
          >
            Become a Sponsor on Zeffy
            <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </Button>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={AAWTA.logoUrl}
                alt="AAWTA logo"
                className="h-12 w-12 rounded-full object-cover ring-2 ring-white/20"
              />
              <div>
                <div className="font-display font-bold text-lg">AAWTA</div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-white/70 font-medium">
                  Workforce Collaborative
                </div>
              </div>
            </div>
            <p className="mt-6 text-white/75 text-[15px] leading-relaxed max-w-md">
              The AAWTA Education &amp; Industry Workforce Collaborative is a regional convener
              connecting education, employers, government, and community leaders to build
              sustainable workforce solutions.
            </p>
            <p className="mt-4 text-white/70 text-[14px] leading-relaxed max-w-md italic">
              Building Pathways. Creating Opportunities. Driving Change.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={s.id}
                  className="w-10 h-10 rounded-full bg-white/8 border border-white/15 flex items-center justify-center hover:bg-aawta-magenta hover:border-aawta-magenta transition-colors duration-200"
                >
                  <s.Icon className="h-4.5 w-4.5" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="overline text-white/70">Navigate</div>
            <ul className="mt-4 space-y-2.5 text-[14px]">
              {NAV_LINKS.map(([label, href]) => (
                <li key={label}>
                  <a
                    href={href}
                    className="text-white/80 hover:text-aawta-magenta transition-colors"
                    data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="overline text-white/70">Stay Updated</div>
            <p className="mt-3 text-white/75 text-[14.5px] leading-relaxed">
              Get sponsor updates, agenda releases, and speaker announcements — straight to
              your inbox.
            </p>
            <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid={FOOTER.newsletterEmail}
                className="bg-white/10 border-white/15 text-white placeholder:text-white/50 rounded-full px-5 h-12 focus-visible:ring-aawta-magenta focus-visible:ring-offset-0 focus-visible:border-aawta-magenta"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid={FOOTER.newsletterSubmit}
                className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium h-12 px-6"
              >
                {loading ? 'Subscribing…' : 'Subscribe'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>

            <div className="mt-8 space-y-2 text-[13.5px] text-white/70">
              <div>{AAWTA.address}</div>
              <div>
                <a href={`tel:${AAWTA.phoneRaw}`} className="hover:text-aawta-magenta">
                  {AAWTA.phone}
                </a>
                <span className="mx-2 text-white/30">·</span>
                <a href={`mailto:${AAWTA.email}`} className="hover:text-aawta-magenta">
                  {AAWTA.email}
                </a>
              </div>
              <div>
                <a
                  href={AAWTA.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-aawta-magenta"
                >
                  www.aawta.org
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-[13px] text-white/60">
          <div>© {new Date().getFullYear()} African American Women Trucking Association. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <a href="#home" className="hover:text-aawta-magenta">Back to top</a>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="text-white/40 hover:text-aawta-magenta transition-colors"
              title="AAWTA team sign-in"
            >
              Admin
            </Link>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Powered by AAWTA</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
